// @ts-nocheck
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Eye, Edit, Trash2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface TipoLocal {
  id: string;
  nome: string;
}

interface Cliente {
  id: string;
  nome: string;
}

interface Empresa {
  id: string;
  razao_social: string;
}

interface Local {
  id: string;
  nome: string;
  descricao?: string;
  tipo_local_id: string;
  cliente_id?: string;
  id_empresa?: string;
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  frete_moto?: number;
  frete_carro_hatch?: number;
  frete_utilitario?: number;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  tipo_local?: {
    nome: string;
  };
  clientes?: {
    nome: string;
  };
  empresas?: {
    razao_social: string;
  };
}

export default function Locais() {
  const { user } = useAuth();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  
  const [locais, setLocais] = useState<Local[]>([]);
  const [tiposLocal, setTiposLocal] = useState<TipoLocal[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTable, setLoadingTable] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedLocal, setSelectedLocal] = useState<Local | null>(null);
  
  const [formData, setFormData] = useState({
    nome: "",
    descricao: "",
    tipo_local_id: "",
    cliente_id: "none",
    id_empresa: "none",
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    frete_moto: "",
    frete_carro_hatch: "",
    frete_utilitario: ""
  });

  const canRead = hasPermission('locais', 'locais', 'read');
  const canWrite = hasPermission('locais', 'locais', 'write');

  // Função específica para carregar tipos de local
  const loadTiposLocal = async () => {
    console.log('=== CARREGANDO TIPOS DE LOCAL ===');
    try {
      // @ts-ignore - Temporary fix for deep type instantiation
      const { data, error } = await supabase
        .from('tipo_local')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome');
      
      console.log('Resultado tipos local - data:', data);
      console.log('Resultado tipos local - error:', error);
      
      if (error) {
        console.error('Erro ao carregar tipos local:', error);
        throw error;
      }
      setTiposLocal(data || []);
      console.log('Tipos local definidos no estado:', data || []);
    } catch (error) {
      console.error('Erro na função loadTiposLocal:', error);
    }
  };

  // Função específica para carregar clientes
  const loadClientes = async () => {
    console.log('=== CARREGANDO CLIENTES ===');
    try {
      const { data, error } = await supabase
        .from('clientes')
        .select('id, nome')
        .eq('ativo', true)
        .order('nome');
      
      console.log('Resultado clientes - data:', data);
      console.log('Resultado clientes - error:', error);
      
      if (error) {
        console.error('Erro ao carregar clientes:', error);
        throw error;
      }
      setClientes(data || []);
      console.log('Clientes definidos no estado:', data || []);
    } catch (error) {
      console.error('Erro na função loadClientes:', error);
    }
  };

  // Função específica para carregar empresas
  const loadEmpresas = async () => {
    console.log('=== CARREGANDO EMPRESAS ===');
    try {
      const { data, error } = await supabase
        .from('empresas')
        .select('id, razao_social')
        .eq('ativo', true)
        .order('razao_social');
      
      console.log('Resultado empresas - data:', data);
      console.log('Resultado empresas - error:', error);
      
      if (error) {
        console.error('Erro ao carregar empresas:', error);
        throw error;
      }
      setEmpresas(data || []);
      console.log('Empresas definidas no estado:', data || []);
    } catch (error) {
      console.error('Erro na função loadEmpresas:', error);
    }
  };

  // Função específica para carregar locais
  const loadLocais = async () => {
    console.log('=== CARREGANDO LOCAIS ===');
    try {
      setLoadingTable(true);
      
      // Primeiro carrega apenas os dados básicos da tabela locais
      const { data: locaisData, error: locaisError } = await supabase
        .from('locais')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('Resultado locais - data:', locaisData);
      console.log('Resultado locais - error:', locaisError);

      if (locaisError) {
        console.error('Erro ao carregar locais:', locaisError);
        throw locaisError;
      }

      // Se conseguiu carregar os locais, agora vamos buscar os dados relacionados
      const locaisCompletos = [];
      
      for (const local of locaisData || []) {
        // Busca o tipo de local
        const { data: tipoLocal } = await supabase
          .from('tipo_local')
          .select('nome')
          .eq('id', local.tipo_local_id)
          .single();

        // Busca o cliente (se existir)
        let cliente = null;
        if (local.cliente_id) {
          const { data: clienteData } = await supabase
            .from('clientes')
            .select('nome')
            .eq('id', local.cliente_id)
            .single();
          cliente = clienteData;
        }

        // Busca a empresa (se existir)
        let empresa = null;
        if (local.id_empresa) {
          const { data: empresaData } = await supabase
            .from('empresas')
            .select('razao_social')
            .eq('id', local.id_empresa)
            .single();
          empresa = empresaData;
        }

        locaisCompletos.push({
          ...local,
          tipo_local: tipoLocal,
          clientes: cliente,
          empresas: empresa
        });
      }

      setLocais(locaisCompletos);
      console.log('Locais completos definidos no estado:', locaisCompletos);
      
    } catch (error) {
      console.error('Erro na função loadLocais:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar lista de locais: " + (error as any)?.message,
        variant: "destructive"
      });
    } finally {
      setLoadingTable(false);
    }
  };

  // Carregar dados quando permitido
  useEffect(() => {
    console.log('=== USEEFFECT INICIAL ===');
    console.log('permissionsLoading:', permissionsLoading);
    console.log('canRead:', canRead);
    if (!permissionsLoading && canRead) {
      console.log('Condições atendidas, carregando dados...');
      loadTiposLocal();
      loadClientes();
      loadEmpresas();
      loadLocais();
    }
  }, [permissionsLoading, canRead]);

  // Função para resetar o formulário
  const resetForm = () => {
    setFormData({
      nome: "",
      descricao: "",
      tipo_local_id: "",
      cliente_id: "none",
      id_empresa: "none",
      cep: "",
      logradouro: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: "",
      frete_moto: "",
      frete_carro_hatch: "",
      frete_utilitario: ""
    });
  };

  // Abrir modal e carregar dados frescos
  const handleOpenCreateModal = () => {
    console.log('=== ABRINDO MODAL ===');
    resetForm();
    setIsCreateModalOpen(true);
    // Recarregar dados para garantir que estão atualizados
    loadTiposLocal();
    loadClientes();
    loadEmpresas();
  };

  // Função para visualizar local
  const handleViewLocal = (local: Local) => {
    setSelectedLocal(local);
    setIsViewModalOpen(true);
  };

  // Função para editar local
  const handleEditLocal = (local: Local) => {
    setSelectedLocal(local);
    setFormData({
      nome: local.nome,
      descricao: local.descricao || "",
      tipo_local_id: local.tipo_local_id,
      cliente_id: local.cliente_id || "none",
      id_empresa: local.id_empresa || "none",
      cep: local.cep || "",
      logradouro: local.logradouro || "",
      numero: local.numero || "",
      complemento: local.complemento || "",
      bairro: local.bairro || "",
      cidade: local.cidade || "",
      estado: local.estado || "",
      frete_moto: local.frete_moto?.toString() || "",
      frete_carro_hatch: local.frete_carro_hatch?.toString() || "",
      frete_utilitario: local.frete_utilitario?.toString() || ""
    });
    setIsEditModalOpen(true);
    // Recarregar dados para garantir que estão atualizados
    loadTiposLocal();
    loadClientes();
    loadEmpresas();
  };

  // Função para atualizar local
  const handleUpdateLocal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocal || !formData.nome || !formData.tipo_local_id) {
      toast({
        title: "Erro",
        description: "Preencha todos os campos obrigatórios",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setLoading(true);
      console.log('=== ATUALIZANDO LOCAL ===');
      console.log('ID do local:', selectedLocal.id);
      
      // Preparar dados de forma simplificada
      const updatePayload = {
        nome: formData.nome.trim(),
        descricao: formData.descricao?.trim() || null,
        tipo_local_id: formData.tipo_local_id,
        cliente_id: formData.cliente_id === "none" ? null : formData.cliente_id,
        id_empresa: formData.id_empresa === "none" ? null : formData.id_empresa,
        cep: formData.cep?.trim() || null,
        logradouro: formData.logradouro?.trim() || null,
        numero: formData.numero?.trim() || null,
        complemento: formData.complemento?.trim() || null,
        bairro: formData.bairro?.trim() || null,
        cidade: formData.cidade?.trim() || null,
        estado: formData.estado?.trim() || null,
        frete_moto: formData.frete_moto && formData.frete_moto.trim() ? Number(formData.frete_moto) : null,
        frete_carro_hatch: formData.frete_carro_hatch && formData.frete_carro_hatch.trim() ? Number(formData.frete_carro_hatch) : null,
        frete_utilitario: formData.frete_utilitario && formData.frete_utilitario.trim() ? Number(formData.frete_utilitario) : null,
        updated_by: user?.id
      };

      console.log('Payload de atualização:', updatePayload);
      
      // Executar a atualização de forma simples
      const { data: updatedData, error: updateError } = await supabase
        .from('locais')
        .update(updatePayload)
        .eq('id', selectedLocal.id)
        .select('id, nome');
        
      console.log('Resultado da atualização:', { updatedData, updateError });
        
      if (updateError) {
        console.error('Erro na atualização:', updateError);
        throw updateError;
      }
      
      toast({
        title: "Sucesso",
        description: "Local atualizado com sucesso!"
      });
      
      // Fechar modal e limpar estados
      setIsEditModalOpen(false);
      resetForm();
      setSelectedLocal(null);
      
      // Recarregar dados
      await loadLocais();
      
    } catch (error: any) {
      console.error('Erro completo:', error);
      toast({
        title: "Erro",
        description: `Erro ao atualizar local: ${error.message || 'Erro desconhecido'}`,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para excluir local
  const handleDeleteLocal = async (localId: string) => {
    try {
      const { error } = await supabase
        .from('locais')
        .delete()
        .eq('id', localId);
        
      if (error) {
        throw error;
      }
      
      toast({
        title: "Sucesso",
        description: "Local excluído com sucesso"
      });
      loadLocais();
    } catch (error) {
      console.error('Error deleting local:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir local: " + (error as any)?.message,
        variant: "destructive"
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('=== SUBMIT ===');
    console.log('formData:', formData);
    if (!formData.nome || !formData.tipo_local_id) {
      toast({
        title: "Erro",
        description: "Preencha todos os campos obrigatórios",
        variant: "destructive"
      });
      return;
    }
    try {
      setLoading(true);
      const dataToSubmit = {
        nome: formData.nome,
        descricao: formData.descricao || null,
        tipo_local_id: formData.tipo_local_id,
        cliente_id: formData.cliente_id === "none" ? null : formData.cliente_id,
        id_empresa: formData.id_empresa === "none" ? null : formData.id_empresa,
        cep: formData.cep || null,
        logradouro: formData.logradouro || null,
        numero: formData.numero || null,
        complemento: formData.complemento || null,
        bairro: formData.bairro || null,
        cidade: formData.cidade || null,
        estado: formData.estado || null,
        frete_moto: formData.frete_moto ? parseFloat(formData.frete_moto) : null,
        frete_carro_hatch: formData.frete_carro_hatch ? parseFloat(formData.frete_carro_hatch) : null,
        frete_utilitario: formData.frete_utilitario ? parseFloat(formData.frete_utilitario) : null,
        ativo: true,
        created_by: user?.id,
        updated_by: user?.id
      };
      console.log('Dados para envio:', dataToSubmit);
      const { error } = await supabase.from('locais').insert([dataToSubmit]);
      if (error) {
        console.error('Erro ao inserir:', error);
        throw error;
      }
      toast({
        title: "Sucesso",
        description: "Local criado com sucesso"
      });
      setIsCreateModalOpen(false);
      resetForm();
      // Recarregar a lista de locais
      loadLocais();
    } catch (error) {
      console.error('Error saving local:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar local: " + (error as any)?.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2">Carregando permissões...</p>
        </div>
      </div>
    );
  }

  if (!canRead) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Acesso Negado</h2>
          <p>Você não tem permissão para acessar esta página.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Locais</h1>
          <p className="text-muted-foreground">Gerencie os locais cadastrados</p>
        </div>
        {canWrite && (
          <Button onClick={handleOpenCreateModal}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Local
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Locais</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder="Buscar locais..."
              className="max-w-sm"
            />
          </div>

          {loadingTable ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Empresa</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Cidade/Estado</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locais.map((local) => (
                  <TableRow key={local.id}>
                    <TableCell className="font-medium">
                      <div>
                        <div>{local.nome}</div>
                        {local.descricao && (
                          <div className="text-sm text-muted-foreground">{local.descricao}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{local.clientes?.nome || '-'}</TableCell>
                    <TableCell>{local.empresas?.razao_social || '-'}</TableCell>
                    <TableCell>{local.tipo_local?.nome || '-'}</TableCell>
                    <TableCell>
                      {local.cidade && local.estado ? `${local.cidade}/${local.estado}` : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={local.ativo ? "default" : "secondary"}>
                        {local.ativo ? "Ativo" : "Inativo"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                       <div className="flex justify-end gap-2">
                         <Button 
                           variant="ghost" 
                           size="icon"
                           onClick={() => handleViewLocal(local)}
                         >
                           <Eye className="h-4 w-4" />
                         </Button>
                         {canWrite && (
                           <Button 
                             variant="ghost" 
                             size="icon"
                             onClick={() => handleEditLocal(local)}
                           >
                             <Edit className="h-4 w-4" />
                           </Button>
                         )}
                         {canWrite && (
                           <AlertDialog>
                             <AlertDialogTrigger asChild>
                               <Button variant="ghost" size="icon">
                                 <Trash2 className="h-4 w-4" />
                               </Button>
                             </AlertDialogTrigger>
                             <AlertDialogContent>
                               <AlertDialogHeader>
                                 <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                 <AlertDialogDescription>
                                   Tem certeza que deseja excluir o local "{local.nome}"? Esta ação não pode ser desfeita.
                                 </AlertDialogDescription>
                               </AlertDialogHeader>
                               <AlertDialogFooter>
                                 <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                 <AlertDialogAction onClick={() => handleDeleteLocal(local.id)}>
                                   Excluir
                                 </AlertDialogAction>
                               </AlertDialogFooter>
                             </AlertDialogContent>
                           </AlertDialog>
                         )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!loadingTable && locais.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum local encontrado
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal para criação de locais */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Local</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Digite o nome do local"
                required
              />
            </div>

            <div>
              <Label htmlFor="tipo_local_id">Tipo de Local *</Label>
              <Select 
                value={formData.tipo_local_id} 
                onValueChange={(value) => setFormData({ ...formData, tipo_local_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo de local" />
                </SelectTrigger>
                <SelectContent>
                  {tiposLocal.map((tipo) => (
                    <SelectItem key={tipo.id} value={tipo.id}>
                      {tipo.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="cliente_id">Cliente (Opcional)</Label>
              <Select 
                value={formData.cliente_id} 
                onValueChange={(value) => setFormData({ ...formData, cliente_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o cliente (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum cliente</SelectItem>
                  {clientes.map((cliente) => (
                    <SelectItem key={cliente.id} value={cliente.id}>
                      {cliente.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="id_empresa">Empresa (Opcional)</Label>
              <Select 
                value={formData.id_empresa} 
                onValueChange={(value) => setFormData({ ...formData, id_empresa: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a empresa (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma empresa</SelectItem>
                  {empresas.map((empresa) => (
                    <SelectItem key={empresa.id} value={empresa.id}>
                      {empresa.razao_social}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Descrição do local"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cep">CEP</Label>
                <Input
                  id="cep"
                  value={formData.cep}
                  onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                  placeholder="00000-000"
                />
              </div>
              <div>
                <Label htmlFor="estado">Estado</Label>
                <Input
                  id="estado"
                  value={formData.estado}
                  onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                  placeholder="SP"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="logradouro">Logradouro</Label>
              <Input
                id="logradouro"
                value={formData.logradouro}
                onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
                placeholder="Rua, Avenida, etc."
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="numero">Número</Label>
                <Input
                  id="numero"
                  value={formData.numero}
                  onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                  placeholder="123"
                />
              </div>
              <div>
                <Label htmlFor="complemento">Complemento</Label>
                <Input
                  id="complemento"
                  value={formData.complemento}
                  onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                  placeholder="Apto, Sala, etc."
                />
              </div>
              <div>
                <Label htmlFor="bairro">Bairro</Label>
                <Input
                  id="bairro"
                  value={formData.bairro}
                  onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                  placeholder="Nome do bairro"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="cidade">Cidade</Label>
              <Input
                id="cidade"
                value={formData.cidade}
                onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                placeholder="Nome da cidade"
              />
            </div>

            <div className="space-y-2">
              <Label>Valores de Frete</Label>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="frete_moto" className="text-sm">Moto</Label>
                  <Input
                    id="frete_moto"
                    type="number"
                    step="0.01"
                    value={formData.frete_moto}
                    onChange={(e) => setFormData({ ...formData, frete_moto: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="frete_carro_hatch" className="text-sm">Carro Hatch</Label>
                  <Input
                    id="frete_carro_hatch"
                    type="number"
                    step="0.01"
                    value={formData.frete_carro_hatch}
                    onChange={(e) => setFormData({ ...formData, frete_carro_hatch: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="frete_utilitario" className="text-sm">Utilitário</Label>
                  <Input
                    id="frete_utilitario"
                    type="number"
                    step="0.01"
                    value={formData.frete_utilitario}
                    onChange={(e) => setFormData({ ...formData, frete_utilitario: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal para visualização de local */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes do Local</DialogTitle>
          </DialogHeader>
          {selectedLocal && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Nome</Label>
                  <p>{selectedLocal.nome}</p>
                </div>
                <div>
                  <Label className="font-semibold">Status</Label>
                  <p>
                    <Badge variant={selectedLocal.ativo ? "default" : "secondary"}>
                      {selectedLocal.ativo ? "Ativo" : "Inativo"}
                    </Badge>
                  </p>
                </div>
              </div>

              {selectedLocal.descricao && (
                <div>
                  <Label className="font-semibold">Descrição</Label>
                  <p>{selectedLocal.descricao}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-semibold">Cliente</Label>
                  <p>{selectedLocal.clientes?.nome || '-'}</p>
                </div>
                <div>
                  <Label className="font-semibold">Empresa</Label>
                  <p>{selectedLocal.empresas?.razao_social || '-'}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label className="font-semibold">Tipo de Local</Label>
                  <p>{selectedLocal.tipo_local?.nome || '-'}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-semibold">Endereço</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">CEP</Label>
                    <p>{selectedLocal.cep || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-sm">Estado</Label>
                    <p>{selectedLocal.estado || '-'}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Logradouro</Label>
                  <p>{selectedLocal.logradouro || '-'}</p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm">Número</Label>
                    <p>{selectedLocal.numero || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-sm">Complemento</Label>
                    <p>{selectedLocal.complemento || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-sm">Bairro</Label>
                    <p>{selectedLocal.bairro || '-'}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Cidade</Label>
                  <p>{selectedLocal.cidade || '-'}</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="font-semibold">Valores de Frete</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm">Moto</Label>
                    <p>{selectedLocal.frete_moto ? `R$ ${selectedLocal.frete_moto.toFixed(2)}` : '-'}</p>
                  </div>
                  <div>
                    <Label className="text-sm">Carro Hatch</Label>
                    <p>{selectedLocal.frete_carro_hatch ? `R$ ${selectedLocal.frete_carro_hatch.toFixed(2)}` : '-'}</p>
                  </div>
                  <div>
                    <Label className="text-sm">Utilitário</Label>
                    <p>{selectedLocal.frete_utilitario ? `R$ ${selectedLocal.frete_utilitario.toFixed(2)}` : '-'}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={() => setIsViewModalOpen(false)}>
                  Fechar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal para edição de local */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Local</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateLocal} className="space-y-4">
            <div>
              <Label htmlFor="edit-nome">Nome *</Label>
              <Input
                id="edit-nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Digite o nome do local"
                required
              />
            </div>

            <div>
              <Label htmlFor="edit-tipo_local_id">Tipo de Local *</Label>
              <Select 
                value={formData.tipo_local_id} 
                onValueChange={(value) => setFormData({ ...formData, tipo_local_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o tipo de local" />
                </SelectTrigger>
                <SelectContent>
                  {tiposLocal.map((tipo) => (
                    <SelectItem key={tipo.id} value={tipo.id}>
                      {tipo.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-cliente_id">Cliente (Opcional)</Label>
              <Select 
                value={formData.cliente_id} 
                onValueChange={(value) => setFormData({ ...formData, cliente_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o cliente (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum cliente</SelectItem>
                  {clientes.map((cliente) => (
                    <SelectItem key={cliente.id} value={cliente.id}>
                      {cliente.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-id_empresa">Empresa (Opcional)</Label>
              <Select 
                value={formData.id_empresa} 
                onValueChange={(value) => setFormData({ ...formData, id_empresa: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a empresa (opcional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhuma empresa</SelectItem>
                  {empresas.map((empresa) => (
                    <SelectItem key={empresa.id} value={empresa.id}>
                      {empresa.razao_social}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-descricao">Descrição</Label>
              <Textarea
                id="edit-descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Descrição do local"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-cep">CEP</Label>
                <Input
                  id="edit-cep"
                  value={formData.cep}
                  onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                  placeholder="00000-000"
                />
              </div>
              <div>
                <Label htmlFor="edit-estado">Estado</Label>
                <Input
                  id="edit-estado"
                  value={formData.estado}
                  onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                  placeholder="SP"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit-logradouro">Logradouro</Label>
              <Input
                id="edit-logradouro"
                value={formData.logradouro}
                onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
                placeholder="Rua, Avenida, etc."
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="edit-numero">Número</Label>
                <Input
                  id="edit-numero"
                  value={formData.numero}
                  onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                  placeholder="123"
                />
              </div>
              <div>
                <Label htmlFor="edit-complemento">Complemento</Label>
                <Input
                  id="edit-complemento"
                  value={formData.complemento}
                  onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                  placeholder="Apto, Sala, etc."
                />
              </div>
              <div>
                <Label htmlFor="edit-bairro">Bairro</Label>
                <Input
                  id="edit-bairro"
                  value={formData.bairro}
                  onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                  placeholder="Nome do bairro"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="edit-cidade">Cidade</Label>
              <Input
                id="edit-cidade"
                value={formData.cidade}
                onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                placeholder="Nome da cidade"
              />
            </div>

            <div className="space-y-2">
              <Label>Valores de Frete</Label>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="edit-frete_moto" className="text-sm">Moto</Label>
                  <Input
                    id="edit-frete_moto"
                    type="number"
                    step="0.01"
                    value={formData.frete_moto}
                    onChange={(e) => setFormData({ ...formData, frete_moto: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-frete_carro_hatch" className="text-sm">Carro Hatch</Label>
                  <Input
                    id="edit-frete_carro_hatch"
                    type="number"
                    step="0.01"
                    value={formData.frete_carro_hatch}
                    onChange={(e) => setFormData({ ...formData, frete_carro_hatch: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-frete_utilitario" className="text-sm">Utilitário</Label>
                  <Input
                    id="edit-frete_utilitario"
                    type="number"
                    step="0.01"
                    value={formData.frete_utilitario}
                    onChange={(e) => setFormData({ ...formData, frete_utilitario: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Atualizando..." : "Atualizar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}